import React, { useState } from "react";

const FullStack = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn to build a
          full-stack web application using the MERN stack (MongoDB, Express.js,
          React, Node.js).
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          HTML, CSS, JavaScript, and programming concepts.
          <p className="font-bold text-sm">Tools Needed:</p> Node.js, MongoDB,
          Visual Studio Code, Postman, Git, and a modern browser.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Full Stack Development",
          objective: "Understand full-stack development and its components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is full-stack development?</li>
                <li>Overview of frontend, backend, and database layers.</li>
                <li>
                  Introduction to the MERN stack (MongoDB, Express.js, React,
                  Node.js).
                </li>
                <li>Client-server architecture.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss real-world examples of full-stack applications.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Setting Up the Development Environment",
          objective: "Set up tools and environment for MERN development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing Node.js and npm.</li>
                <li>
                  Installing Visual Studio Code and useful extensions (e.g.,
                  Prettier, ESLint).
                </li>
                <li>Overview of Git for version control.</li>
                <li>Creating a basic folder structure for a MERN project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Node.js, npm, and VS Code.</li>
                <li>Set up a Git repository.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Introduction to Node.js",
          objective: "Understand the basics of Node.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Node.js? Why use it?</li>
                <li>Event-driven architecture and non-blocking I/O.</li>
                <li>Running JavaScript outside the browser.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Write and run a simple Node.js script (e.g., a "Hello World"
                  console log).
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Node.js Modules and npm",
          objective: "Learn how to use Node.js modules and npm.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding Node.js modules (built-in and external).</li>
                <li>Introduction to npm (Node Package Manager).</li>
                <li>Installing and managing dependencies.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Initialize a Node.js project with npm init.</li>
                <li>Install a third-party package (e.g., nodemon).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Introduction to Express.js",
          objective: "Learn the basics of Express.js for backend development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Express.js? Why use it?</li>
                <li>Setting up an Express server.</li>
                <li>Handling HTTP requests (GET, POST, etc.).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a simple Express server with a / route that responds
                  with "Hello World".
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Express.js Routing Basics",
          objective: "Understand routing in Express.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Defining routes in Express.js.</li>
                <li>Handling multiple routes (e.g., /about, /contact).</li>
                <li>Route parameters and query strings.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Add routes for /about and /user/:id to the Express server.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Express.js Middleware",
          objective: "Learn how to use middleware in Express.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is middleware?</li>
                <li>Built-in vs. custom middleware.</li>
                <li>Using middleware for logging and request processing.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create custom middleware to log incoming requests.</li>
                <li>Use express.json() middleware to parse JSON bodies.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Introduction to MongoDB",
          objective: "Understand the basics of MongoDB.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is a NoSQL database?</li>
                <li>MongoDB vs. SQL databases.</li>
                <li>Key concepts: collections, documents, and schemas.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss examples of MongoDB use cases.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Setting Up MongoDB",
          objective: "Set up MongoDB for development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Installing MongoDB locally or using MongoDB Atlas (cloud).
                </li>
                <li>
                  Connecting to MongoDB using the MongoDB shell or Compass.
                </li>
                <li>Creating a database and collection.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Set up a MongoDB database and create a "users" collection.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: MongoDB CRUD Operations",
          objective: "Learn CRUD operations in MongoDB.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>CRUD operations: Create, Read, Update, Delete.</li>
                <li>
                  MongoDB queries (e.g., find, insertOne, updateOne, deleteOne).
                </li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Perform basic CRUD operations on the "users" collection using
                  MongoDB Compass or shell.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Connecting MongoDB to Node.js",
          objective: "Integrate MongoDB with Node.js using Mongoose.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Mongoose (ODM for MongoDB).</li>
                <li>Installing and setting up Mongoose.</li>
                <li>Connecting Express.js to MongoDB.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Connect the Express server to MongoDB using Mongoose.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Mongoose Schemas and Models",
          objective: "Learn how to define schemas and models in Mongoose.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Defining schemas in Mongoose.</li>
                <li>Creating models from schemas.</li>
                <li>Data validation in Mongoose schemas.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a "User" schema and model for the "users" collection.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Building a RESTful API – GET",
          objective: "Build a RESTful API to read data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is a RESTful API?</li>
                <li>Building a GET endpoint to fetch all users.</li>
                <li>Handling errors in API responses.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a /api/users GET endpoint to fetch all users from
                  MongoDB.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Building a RESTful API – POST",
          objective: "Build a RESTful API to create data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building a POST endpoint to add a new user.</li>
                <li>Validating request bodies.</li>
                <li>Sending appropriate HTTP status codes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a /api/users POST endpoint to add a new user.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Building a RESTful API – PUT and DELETE",
          objective: "Build a RESTful API to update and delete data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building a PUT endpoint to update a user.</li>
                <li>Building a DELETE endpoint to remove a user.</li>
                <li>Using route parameters for specific resources.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create /api/users/:id PUT and DELETE endpoints.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Error Handling in Express.js",
          objective: "Learn error handling in Express.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling synchronous and asynchronous errors.</li>
                <li>Creating custom error middleware.</li>
                <li>Sending error responses with appropriate status codes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add error handling to the users API.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Data Validation in Express.js",
          objective: "Learn how to validate API inputs.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to data validation.</li>
                <li>Using Joi or Express Validator for validation.</li>
                <li>Implementing validation middleware.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Add validation to the POST and PUT endpoints of the users API.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Environment Variables and Configuration",
          objective: "Learn how to manage configuration in Express.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are environment variables?</li>
                <li>Using the dotenv package.</li>
                <li>
                  Storing sensitive information (e.g., MongoDB connection
                  string).
                </li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Set up environment variables for the MongoDB connection.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Basic Security Practices in Express.js",
          objective: "Learn basic security practices for Express.js.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Helmet to secure HTTP headers.</li>
                <li>Setting up CORS for cross-origin requests.</li>
                <li>
                  Preventing common security vulnerabilities (e.g., XSS, CSRF).
                </li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add Helmet and CORS to the Express server.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Introduction to React",
          objective: "Understand the basics of React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is React? Why use it?</li>
                <li>Components, props, and state.</li>
                <li>JSX syntax and rendering.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss examples of React applications.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Setting Up a React Project",
          objective: "Set up a React frontend for the MERN application.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up a React project using Create React App.</li>
                <li>Understanding React folder structure.</li>
                <li>Running a React app locally.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a React project using Create React App.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Building React Components",
          objective: "Learn how to create React components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Functional components vs. class components.</li>
                <li>Creating and rendering components.</li>
                <li>Reusing components with props.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a "Hello World" component and a reusable "Navbar"
                  component.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Styling in React",
          objective: "Learn how to style React components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Styling options in React (inline CSS, CSS modules,
                  styled-components, Tailwind CSS).
                </li>
                <li>Applying styles to components.</li>
                <li>Organizing styles in a React project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Style the "Navbar" component using CSS modules or Tailwind
                  CSS.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: React State with useState",
          objective: "Learn state management in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is state in React?</li>
                <li>Using the useState hook.</li>
                <li>Updating state and re-rendering.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a counter component to increment and decrement a number
                  using state.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Event Handling in React",
          objective: "Learn how to handle events in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Handling events (clicks, form submissions, input changes).
                </li>
                <li>Passing event handlers as props.</li>
                <li>Preventing default behavior.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a form component to capture user input and display it.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Conditional Rendering in React",
          objective: "Learn how to render components conditionally.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Conditional rendering with if, &&, and ternary operators.
                </li>
                <li>Rendering based on state or props.</li>
                <li>Handling loading and error states.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a component that shows or hides content based on a
                  button click.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Lists and Keys in React",
          objective: "Learn how to render lists in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rendering lists using map.</li>
                <li>Understanding the importance of keys in lists.</li>
                <li>Filtering and sorting lists.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a component to display a list of users from mock data.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Introduction to React Router",
          objective: "Learn the basics of client-side routing in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is React Router?</li>
                <li>Setting up React Router.</li>
                <li>Defining routes and navigation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Set up React Router and create routes for Home and About
                  pages.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Advanced React Router",
          objective: "Learn advanced routing features in React Router.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Nested routes and dynamic routing.</li>
                <li>
                  Navigation using Link, NavLink, and programmatic navigation.
                </li>
                <li>Handling 404 pages for unmatched routes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a "Users" route with dynamic routing (/users/:id).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Making API Calls in React",
          objective: "Learn how to fetch data from APIs in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using fetch or Axios for API calls.</li>
                <li>Fetching data from the backend API.</li>
                <li>Displaying data in React components.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Fetch and display the list of users from the backend API.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Handling Loading and Error States",
          objective: "Learn how to manage loading and error states in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling loading states during API calls.</li>
                <li>Displaying error messages to users.</li>
                <li>
                  Using conditional rendering for loading and error states.
                </li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Add loading and error states to the users list component.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: POST Requests in React",
          objective: "Learn how to send POST requests from React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building a form to submit data to the backend.</li>
                <li>Handling form submissions in React.</li>
                <li>Sending POST requests using fetch or Axios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build a form to add a new user and submit it to the backend.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: PUT and DELETE Requests in React",
          objective: "Learn how to send PUT and DELETE requests from React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building components to update and delete data.</li>
                <li>Sending PUT and DELETE requests using fetch or Axios.</li>
                <li>Updating the UI after successful requests.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Add edit and delete buttons to the users list component.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Introduction to State Management",
          objective: "Understand state management in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Why is state management important?</li>
                <li>
                  Options for state management (Context API, Redux, etc.).
                </li>
                <li>Introduction to the Context API.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Discuss examples of state management in real-world
                  applications.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Using the Context API",
          objective: "Learn how to use the Context API for global state.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a context in React.</li>
                <li>Providing and consuming context.</li>
                <li>Managing global state with Context API.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a context to manage the list of users globally.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Using useReducer for Complex State",
          objective:
            "Learn how to use useReducer for complex state management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is useReducer?</li>
                <li>Combining useReducer with Context API.</li>
                <li>Managing complex state logic.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Implement a reducer to handle add, update, and delete actions
                  for users.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Best Practices in State Management",
          objective: "Learn best practices for state management in React.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Organizing state in a React application.</li>
                <li>Avoiding common pitfalls in state management.</li>
                <li>When to use Context API vs. Redux.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Refactor the users app to use global state with Context API
                  and useReducer.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Preparing the MERN App for Deployment",
          objective: "Learn how to prepare a MERN app for deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building the React frontend for production.</li>
                <li>Serving the frontend from the Express backend.</li>
                <li>Configuring the backend for production.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Build the React frontend and serve it from the Express server.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Deploying the Backend",
          objective: "Learn how to deploy the backend.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deployment options (Heroku, Render, Vercel, etc.).</li>
                <li>Deploying the Express backend to a hosting platform.</li>
                <li>Configuring MongoDB Atlas for cloud deployment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy the Express backend to Render or Heroku.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Deploying the Frontend",
          objective: "Learn how to deploy the frontend.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploying the React frontend to Vercel or Netlify.</li>
                <li>
                  Connecting the deployed frontend to the deployed backend.
                </li>
                <li>Testing the deployed application.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Deploy the React frontend to Vercel and test the full
                  application.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Introduction to CI/CD",
          objective: "Understand basic CI/CD concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CI/CD?</li>
                <li>Using GitHub Actions for automated deployment.</li>
                <li>Setting up a basic CI/CD pipeline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Set up a GitHub Actions workflow for automated deployment.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., Task Manager, Blog, E-commerce
                  app).
                </li>
                <li>
                  Planning the database schema, API endpoints, and UI
                  components.
                </li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a project plan and wireframe for the final project.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Backend Development",
          objective: "Build the backend for the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Designing the database schema.</li>
                <li>Building the API endpoints.</li>
                <li>Testing the API using Postman.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the backend for the final project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Frontend Development",
          objective: "Build the frontend for the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Designing the UI components.</li>
                <li>Connecting the frontend to the backend API.</li>
                <li>Implementing state management.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the frontend for the final project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Deployment and Presentation",
          objective: "Deploy and present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploying the full-stack application.</li>
                <li>Testing the deployed application.</li>
                <li>Presenting the project and discussing challenges.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy the final project and present it.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Build a full-stack web application using the MERN stack.</li>
            <li>Create RESTful APIs with Express.js and MongoDB.</li>
            <li>Develop dynamic and interactive frontends with React.</li>
            <li>Manage state effectively in React applications.</li>
            <li>Deploy a MERN application to a production environment.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Task Manager:</span> A to-do
              list app with user authentication, task CRUD, and categories.
            </li>
            <li>
              <span className="font-bold text-sm">Blog Platform:</span> A
              blogging app with posts, comments, and user profiles.
            </li>
            <li>
              <span className="font-bold text-sm">E-commerce Store:</span> A
              simple store with products, cart, and checkout functionality.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Encourage learners to practice concepts outside of class hours
              using exercises or mini-projects.
            </li>
            <li>
              Provide resources (e.g., documentation links, cheat sheets) for
              further learning.
            </li>
            <li>
              Use the discussion time in each session to address questions and
              clarify doubts.
            </li>
          </ul>
          <p className="text-sm">
            This syllabus is designed to be beginner-friendly, with a gradual
            progression from basic concepts to advanced topics, ensuring
            learners gain practical skills in full-stack development over the
            45-day period.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-8 text-center">
        <h1 className="text-2xl font-bold mb-1">Full Stack MERN Syllabus</h1>
        <p className="text-sm">
          Master Full-Stack Development with MERN Stack (45 Hours – 1 Hour Per
          Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>
      <div className="max-w-4xl mx-auto p-4 pt-20 pb-20">
        {/* Course Overview */}
        <div className="mb-4 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-2 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-2 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-4 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-2 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-2 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-2 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-4 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-2 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-2 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-4 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-2 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-2 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-4 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-2 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-2 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullStack;
